import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useSiteSettings } from "../../context/SiteSettingsContext";
import { Button } from "../ui/button";
import {
  Truck,
  LayoutDashboard,
  FileText,
  User,
  Building2,
  Users,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Car,
  CreditCard,
  Settings,
  Receipt,
  Tag,
  Mail,
  MessageSquare,
  HeadphonesIcon,
  UserCheck,
  Palette,
  Euro,
  Layers
} from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "../../lib/utils";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function DashboardLayout({ children, title }) {
  const { user, logout, token } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationCounts, setNotificationCounts] = useState({
    admin_new_tickets: 0,
    company_ticket_updates: 0
  });

  // Fetch notification counts
  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/notifications/count`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setNotificationCounts(res.data);
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchNotifications();
    // Refresh every 30 seconds
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Get notification count for a specific route
  const getNotificationCount = (route) => {
    if (user?.role === "admin" && route === "/admin/ticket") {
      return notificationCounts.admin_new_tickets;
    }
    if (user?.role === "company" && route === "/company/supporto") {
      return notificationCounts.company_ticket_updates;
    }
    return 0;
  };

  const getNavItems = () => {
    if (user?.role === "admin") {
      return [
        { to: "/admin", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/admin/utenti", icon: Users, label: "Utenti" },
        { to: "/admin/aziende", icon: Building2, label: "Aziende" },
        { to: "/admin/agenti", icon: UserCheck, label: "Agenti Commerciali" },
        { to: "/admin/veicoli", icon: Truck, label: "Veicoli" },
        { to: "/admin/categorie-veicoli", icon: Layers, label: "Categorie Veicoli" },
        { to: "/admin/prenotazioni", icon: FileText, label: "Prenotazioni" },
        { to: "/admin/abbonamenti", icon: CreditCard, label: "Abbonamenti" },
        { to: "/admin/transazioni", icon: Receipt, label: "Transazioni" },
        { to: "/admin/sconti", icon: Tag, label: "Codici Sconto" },
        { to: "/admin/ticket", icon: MessageSquare, label: "Ticket Supporto" },
        { to: "/admin/email", icon: Mail, label: "Gestione Email" },
        { to: "/admin/sito", icon: Palette, label: "Personalizza Sito" },
        { to: "/admin/impostazioni", icon: Settings, label: "Impostazioni" },
      ];
    } else if (user?.role === "company") {
      return [
        { to: "/company", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/company/veicoli", icon: Truck, label: "Veicoli" },
        { to: "/company/tariffe", icon: Euro, label: "Tariffe" },
        { to: "/company/prenotazioni", icon: FileText, label: "Prenotazioni" },
        { to: "/company/abbonamento", icon: CreditCard, label: "Abbonamento" },
        { to: "/company/supporto", icon: HeadphonesIcon, label: "Supporto" },
        { to: "/company/profilo", icon: Building2, label: "Profilo Azienda" },
      ];
    } else {
      return [
        { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { to: "/prenotazioni", icon: FileText, label: "Prenotazioni" },
        { to: "/profilo", icon: User, label: "Profilo" },
      ];
    }
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 bg-background border-b border-border">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center gap-2">
            {siteSettings.logo_url ? (
              <img src={siteSettings.logo_url} alt="Logo" className="h-8 object-contain" />
            ) : (
              <>
                <div className="bg-primary p-1.5">
                  <Truck className="h-5 w-5 text-primary-foreground" />
                </div>
                <span className="font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  NOLEGGIO<span className="text-primary">FURGONI</span>
                </span>
              </>
            )}
          </Link>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={cn(
            "fixed lg:sticky top-0 left-0 z-40 h-screen w-64 bg-background border-r border-border transition-transform lg:translate-x-0 flex flex-col",
            sidebarOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          {/* Logo - Desktop */}
          <div className="hidden lg:flex items-center gap-2 px-6 h-16 border-b border-border flex-shrink-0">
            <Link to="/" className="flex items-center gap-2">
              {siteSettings.logo_url ? (
                <img src={siteSettings.logo_url} alt="Logo" className="h-8 object-contain" />
              ) : (
                <>
                  <div className="bg-primary p-1.5">
                    <Truck className="h-5 w-5 text-primary-foreground" />
                  </div>
                  <span className="font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    NOLEGGIO<span className="text-primary">FURGONI</span>
                  </span>
                </>
              )}
            </Link>
          </div>

          {/* User Info */}
          <div className="p-4 border-b border-border mt-16 lg:mt-0 flex-shrink-0">
            <div className="flex items-center gap-3">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-bold">
                  {user?.name?.charAt(0).toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <div className="mt-2">
              <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium uppercase tracking-wider bg-primary/10 text-primary">
                {user?.role}
              </span>
            </div>
          </div>

          {/* Navigation - Scrollable */}
          <nav className="flex-1 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.to;
              const notifCount = getNotificationCount(item.to);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-colors relative",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent text-muted-foreground hover:text-foreground"
                  )}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <item.icon className="h-5 w-5" />
                  {item.label}
                  {notifCount > 0 && (
                    <span className={cn(
                      "ml-auto flex h-5 min-w-5 items-center justify-center rounded-full text-xs font-bold",
                      isActive ? "bg-primary-foreground text-primary" : "bg-red-500 text-white"
                    )}>
                      {notifCount > 99 ? "99+" : notifCount}
                    </span>
                  )}
                  {isActive && notifCount === 0 && <ChevronRight className="h-4 w-4 ml-auto" />}
                </Link>
              );
            })}
          </nav>

          {/* Footer Actions - Fixed at bottom */}
          <div className="p-4 border-t border-border flex-shrink-0 bg-background">
            <Link
              to="/catalogo"
              className="flex items-center gap-3 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-2"
            >
              <Truck className="h-5 w-5" />
              Catalogo Veicoli
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={handleLogout}
              data-testid="sidebar-logout"
            >
              <LogOut className="h-5 w-5 mr-3" />
              Esci
            </Button>
          </div>
        </aside>

        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Page Header */}
          {title && (
            <div className="bg-background border-b border-border px-6 py-4">
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {title}
              </h1>
            </div>
          )}

          {/* Page Content */}
          <div className="p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
